import { cn } from '../../utils/utils';

export interface ProgressBarProps {
  value: number;
  max?: number;
  variant?: 'default' | 'success' | 'warning' | 'error';
  size?: 'sm' | 'md' | 'lg';
  /** Renders a "Progress — n%" row above the track. */
  showLabel?: boolean;
  /** The bar's accessible name. Required unless a label names it elsewhere. */
  ariaLabel?: string;
  /**
   * Overrides the percentage a screen reader announces. For a bar measuring
   * something that isn't a percentage — "Step 2 of 4: Lessons" — the raw number
   * is meaningless out loud.
   */
  ariaValueText?: string;
  /** Lower bound. Non-zero only for bars that start at 1, e.g. step counters. */
  min?: number;
  className?: string;
  /**
   * Overrides the fill colour with a class, for bars whose colour is data-driven
   * rather than one of the four semantic `variant`s — a mastery ramp, a
   * per-category legend. Wins over `variant` via `cn`.
   */
  fillClassName?: string;
  /**
   * Overrides the fill colour with a raw CSS colour, for the case the class
   * form cannot serve: a value computed at runtime, e.g. a chart palette keyed
   * by index. Prefer `fillClassName` — a class keeps the colour in the token
   * system, an inline style leaves it.
   */
  fillColor?: string;
}

/**
 * `sm` is `h-1.5`, not `h-1`: at 4px a track reads as a hairline rule rather
 * than a bar, and 6px is the value the wizard's step progress was designed at.
 */
const sizeClasses = {
  sm: 'h-1.5',
  md: 'h-2',
  lg: 'h-3',
};

/**
 * Tokens, not palette classes. This used to be `bg-blue-600 dark:bg-primary`,
 * which was wrong twice: a raw blue competing with the indigo brand accent in
 * light, and `--primary` in dark — that token is the near-black ACTION fill in
 * light mode, so the pair described two unrelated colours.
 *
 * `default` is the brand ramp and so needs an explicit dark step. The other
 * three are semantic status tokens that redefine themselves under `.dark` in
 * globals.css, so a `dark:` counterpart here would just fight them.
 */
const variantColors = {
  default: 'bg-brand-600 dark:bg-brand-400',
  success: 'bg-success',
  warning: 'bg-warning',
  error: 'bg-destructive',
};

export function ProgressBar({
  value,
  max = 100,
  variant = 'default',
  size = 'md',
  showLabel = false,
  ariaLabel,
  ariaValueText,
  min = 0,
  className,
  fillClassName,
  fillColor,
}: ProgressBarProps) {
  const span = max - min || 1;
  const percentage = Math.min(Math.max(((value - min) / span) * 100, 0), 100);

  return (
    <div className={cn('w-full', className)}>
      {showLabel && (
        <div className='text-muted-foreground mb-1 flex justify-between text-sm'>
          <span>Progress</span>
          <span>{Math.round(percentage)}%</span>
        </div>
      )}
      {/* `role='progressbar'` sits on the TRACK, which is the element whose
          width the value describes. Without it this rendered as two anonymous
          divs and announced nothing at all — the reason the onboarding wizard
          hand-rolled its own bar rather than reusing this one. */}
      <div
        role='progressbar'
        aria-label={ariaLabel}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-valuetext={ariaValueText}
        className={cn(
          'bg-secondary w-full overflow-hidden rounded-full',
          sizeClasses[size]
        )}
      >
        <div
          className={cn(
            'h-full rounded-full transition-all duration-300 ease-in-out',
            variantColors[variant],
            fillClassName
          )}
          style={{ width: `${percentage}%`, backgroundColor: fillColor }}
        />
      </div>
    </div>
  );
}
